import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DollarSign, TrendingUp, Calendar, Users, User, AlertCircle, Clock,
  FileText, CheckCircle, CreditCard, HeartHandshake, Building, Shield,
  Check, X, Wrench, Truck, Shirt, Car, Navigation, MapPin
} from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../services/api';

const OverviewTab = ({
  analytics,
  bookings,
  workers,
  pendingApplications,
  setActiveTab,
  handleApproveApplication,
  handleRejectApplication
}) => {
  const [rideStats, setRideStats] = useState(null);
  const [rides, setRides] = useState([]);
  const [loadingRides, setLoadingRides] = useState(true);

  useEffect(() => {
    const fetchRideData = async () => {
      try {
        const [ridesRes, statsRes] = await Promise.all([
          api.get('/rides/').catch(() => ({ data: [] })),
          api.get('/rides/stats/overview').catch(() => ({ data: {} }))
        ]);
        setRides(Array.isArray(ridesRes.data) ? ridesRes.data : []);
        setRideStats(statsRes.data || {});
      } catch (error) {
        console.error('Failed to fetch ride data:', error);
      }
      setLoadingRides(false);
    };
    fetchRideData();
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="overview-tab">
      <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond'] mb-8">Dashboard Overview</h1>
      
      {/* Primary Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6 mb-6">
        <div 
          className="stats-card cursor-pointer hover:ring-2 hover:ring-[var(--success)] transition-all relative group"
          onClick={() => setActiveTab('funds')}
          data-testid="overview-revenue-card"
        >
          <div className="flex items-center justify-between mb-3">
            <DollarSign className="w-8 h-8 text-[var(--success)]" />
            <TrendingUp className="w-4 h-4 text-[var(--success)]" />
          </div>
          <p className="text-2xl lg:text-3xl font-bold">${analytics?.total_collected || analytics?.total_revenue || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Collected</p>
          {/* Revenue Breakdown Tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            <div className="bg-gray-900 text-white text-xs rounded-lg p-3 whitespace-nowrap shadow-lg">
              <div className="font-semibold mb-1 border-b border-gray-700 pb-1">Revenue Breakdown</div>
              <div className="space-y-1">
                <div className="flex justify-between gap-4">
                  <span>Fully Paid:</span>
                  <span className="text-green-400">${analytics?.total_revenue || 0}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Deposits:</span>
                  <span className="text-blue-400">${analytics?.total_deposits || 0}</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-gray-700 pt-1 font-semibold">
                  <span>Remaining Due:</span>
                  <span className="text-amber-400">${analytics?.remaining_due || 0}</span>
                </div>
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
        
        <div 
          className="stats-card cursor-pointer hover:ring-2 hover:ring-[var(--primary)] transition-all"
          onClick={() => setActiveTab('bookings')}
          data-testid="overview-bookings-card"
        >
          <div className="flex items-center justify-between mb-3">
            <Calendar className="w-8 h-8 text-[var(--primary)]" />
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{bookings?.length || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Bookings</p>
        </div>
        
        {/* NEW: Rides Stats Card */}
        <div 
          className="stats-card cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all relative group"
          onClick={() => setActiveTab('rides')}
          data-testid="overview-rides-card"
        >
          <div className="flex items-center justify-between mb-3">
            <Car className="w-8 h-8 text-blue-500" />
            {rides.filter(r => ['requested', 'pending_driver', 'driver_assigned', 'en_route_pickup', 'arrived_pickup', 'in_progress'].includes(r.status)).length > 0 && (
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            )}
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{rides?.length || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Rides</p>
          {/* Rides Breakdown Tooltip */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            <div className="bg-gray-900 text-white text-xs rounded-lg p-3 whitespace-nowrap shadow-lg">
              <div className="font-semibold mb-1 border-b border-gray-700 pb-1">Rides Breakdown</div>
              <div className="space-y-1">
                <div className="flex justify-between gap-4">
                  <span>Active:</span>
                  <span className="text-blue-400">{rides.filter(r => ['in_progress', 'en_route_pickup', 'arrived_pickup'].includes(r.status)).length}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Pending:</span>
                  <span className="text-yellow-400">{rides.filter(r => ['requested', 'pending_driver', 'driver_assigned'].includes(r.status)).length}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>Completed:</span>
                  <span className="text-green-400">{rides.filter(r => r.status === 'completed').length}</span>
                </div>
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
        
        <div 
          className="stats-card cursor-pointer hover:ring-2 hover:ring-[var(--secondary)] transition-all"
          onClick={() => setActiveTab('workers')}
          data-testid="overview-workers-card"
        >
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-[var(--secondary)]" />
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{workers?.length || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Active Workers</p>
        </div>
        
        <div 
          className="stats-card cursor-pointer hover:ring-2 hover:ring-[#D4AF37] transition-all"
          onClick={() => setActiveTab('services')}
          data-testid="overview-customers-card"
        >
          <div className="flex items-center justify-between mb-3">
            <User className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <p className="text-2xl lg:text-3xl font-bold">{analytics?.total_customers || bookings?.filter((b, i, arr) => arr.findIndex(x => x.customer_id === b.customer_id) === i).length || 0}</p>
          <p className="text-sm text-[var(--text-muted)]">Total Customers</p>
        </div>
      </div>

      {/* Secondary Stats - Pending/Action Items */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        <div 
          className="card p-4 cursor-pointer hover:shadow-lg transition-all border-l-4 border-amber-500"
          onClick={() => setActiveTab('applications')}
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-amber-500" />
            <div>
              <p className="text-xl font-bold text-amber-600">{pendingApplications?.length || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Pending Applications</p>
            </div>
          </div>
        </div>
        
        <div 
          className="card p-4 cursor-pointer hover:shadow-lg transition-all border-l-4 border-[#D4AF37]"
          onClick={() => setActiveTab('timerecords')}
        >
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-[#D4AF37]" />
            <div>
              <p className="text-xl font-bold text-[#D4AF37]">{bookings?.filter(b => b.status === 'in_progress').length || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">In Progress</p>
            </div>
          </div>
        </div>
        
        <div 
          className="card p-4 cursor-pointer hover:shadow-lg transition-all border-l-4 border-purple-500"
          onClick={() => setActiveTab('onboarding')}
        >
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-[#F4C2C2]" />
            <div>
              <p className="text-xl font-bold text-[#F4C2C2]">{workers?.filter(w => !w.is_verified).length || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Pending Onboarding</p>
            </div>
          </div>
        </div>
        
        <div 
          className="card p-4 cursor-pointer hover:shadow-lg transition-all border-l-4 border-[var(--success)]"
          onClick={() => setActiveTab('bookings')}
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[var(--success)]" />
            <div>
              <p className="text-xl font-bold text-[var(--success)]">{bookings?.filter(b => b.status === 'completed').length || 0}</p>
              <p className="text-xs text-[var(--text-muted)]">Completed Jobs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Service Categories Section */}
      <div className="grid lg:grid-cols-5 gap-4 mb-8">
        {/* Rides Category - NEW */}
        <div 
          className="card p-5 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
          onClick={() => setActiveTab('rides')}
          data-testid="overview-rides-section"
        >
          <h3 className="font-bold text-base mb-3 flex items-center gap-2">
            <Car className="w-5 h-5 text-blue-500" />
            Rides
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-3">Transportation & mobility</p>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Total Rides</span>
              <span className="font-bold">{rides?.length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">In Progress</span>
              <span className="font-bold text-blue-500">{rides?.filter(r => ['in_progress', 'en_route_pickup', 'arrived_pickup'].includes(r.status)).length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Completed</span>
              <span className="font-bold text-[var(--success)]">{rides?.filter(r => r.status === 'completed').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Revenue</span>
              <span className="font-bold text-[var(--success)]">${rideStats?.total_revenue?.toFixed(2) || rides?.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.final_fare || r.estimated_fare || 0), 0).toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Available Drivers</span>
              <span className="font-bold">{workers?.filter(w => w.is_driver && w.is_available).length || 0}</span>
            </div>
          </div>
        </div>

        {/* Laundry Card */}
        <div 
          className="card p-5 cursor-pointer hover:ring-2 hover:ring-[#F4C2C2] transition-all"
          onClick={() => setActiveTab('services')}
        >
          <h3 className="font-bold text-base mb-3 flex items-center gap-2">
            <Shirt className="w-5 h-5 text-[#F4C2C2]" />
            Laundry
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-3">Wash & fold, press & iron service</p>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Total Bookings</span>
              <span className="font-bold">{bookings?.filter(b => b.service_type === 'laundry').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Completed</span>
              <span className="font-bold text-[var(--success)]">{bookings?.filter(b => b.service_type === 'laundry' && b.status === 'completed').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Workers</span>
              <span className="font-bold">{workers?.filter(w => w.service_types?.includes('laundry')).length || 0}</span>
            </div>
          </div>
        </div>

        <div 
          className="card p-5 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
          onClick={() => setActiveTab('courier')}
        >
          <h3 className="font-bold text-base mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-500" />
            Courier
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-3">Package delivery, documents, express</p>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Total Bookings</span>
              <span className="font-bold">{bookings?.filter(b => b.service_type === 'courier').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Completed</span>
              <span className="font-bold text-[var(--success)]">{bookings?.filter(b => b.service_type === 'courier' && b.status === 'completed').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Workers</span>
              <span className="font-bold">{workers?.filter(w => w.service_types?.includes('courier')).length || 0}</span>
            </div>
          </div>
        </div>
        
        {/* Errands & Tasks Card - Combined */}
        <div 
          className="card p-5 cursor-pointer hover:ring-2 hover:ring-rose-500 transition-all"
          onClick={() => setActiveTab('services')}
        >
          <h3 className="font-bold text-base mb-3 flex items-center gap-2">
            <HeartHandshake className="w-5 h-5 text-rose-500" />
            Errands & Tasks
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-3">Grocery, prescriptions, assembly, yard work</p>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Total Bookings</span>
              <span className="font-bold">{(bookings?.filter(b => b.service_type === 'errands' || b.service_type === 'tasks').length) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Completed</span>
              <span className="font-bold text-[var(--success)]">{(bookings?.filter(b => (b.service_type === 'errands' || b.service_type === 'tasks') && b.status === 'completed').length) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Workers</span>
              <span className="font-bold">{workers?.filter(w => w.service_types?.includes('errands') || w.service_types?.includes('tasks')).length || 0}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Payment Summary Row */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[var(--primary)]" />
            Payment Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Paid</span>
              <span className="font-bold text-[var(--success)]">{bookings?.filter(b => b.payment_status === 'paid').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Deposit Paid</span>
              <span className="font-bold text-[#D4AF37]">{bookings?.filter(b => b.payment_status === 'deposit_paid').length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-[var(--text-muted)]">Pending</span>
              <span className="font-bold text-amber-600">{bookings?.filter(b => b.payment_status === 'pending').length || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="card p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[var(--secondary)]" />
            Today's Schedule
          </h3>
          <div className="space-y-2">
            {bookings?.filter(b => {
              const today = new Date().toISOString().split('T')[0];
              return b.scheduled_date === today;
            }).slice(0, 3).map((booking, idx) => (
              <div key={booking.id || idx} className="text-sm p-2 bg-[var(--background-subtle)] rounded">
                <p className="font-medium truncate">{booking.service_name}</p>
                <p className="text-xs text-[var(--text-muted)]">{booking.scheduled_time}</p>
              </div>
            ))}
            {bookings?.filter(b => {
              const today = new Date().toISOString().split('T')[0];
              return b.scheduled_date === today;
            }).length === 0 && (
              <p className="text-sm text-[var(--text-muted)]">No bookings scheduled for today</p>
            )}
          </div>
        </div>
        
        <div className="card p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Pending Applications
          </h3>
          {pendingApplications?.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No pending applications</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {pendingApplications?.slice(0, 3).map((app, idx) => (
                <div key={app.id || idx} className="flex items-center justify-between p-2 bg-[var(--warning)]/10 rounded">
                  <div>
                    <p className="font-medium text-sm">{app.full_name}</p>
                    <p className="text-xs text-[var(--text-muted)]">{app.service_types?.join(', ')}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      onClick={() => handleApproveApplication(app.id)}
                      className="bg-[var(--success)] hover:bg-[var(--success)]/90"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleRejectApplication(app.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default OverviewTab;
